import { useKnockClient } from "@knocklabs/react";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useCallback } from "react";

import {
  KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE,
  type ReviewPromptActionType,
} from "./constants";

/**
 * Stamps the rating-prompt cooldown pointer for a terminal mood-step action
 * (No → "decline", Ask me later → "dismiss"). Writes `lastReviewPromptShownAt`
 * = now and `lastReviewPromptActionType` in a single identify; the dashboard
 * gates the per-type re-prompt window off that pair. It is a pure write with no
 * preceding read — the window lives in dashboard targeting, not in code — so a
 * user who bails before any follow-up step is still recorded. The rate path
 * stamps the same pointer from `useRequestInAppReview` alongside the native
 * request.
 */
export function useRecordReviewPromptCooldown(): (
  actionType: ReviewPromptActionType
) => Promise<void> {
  const knockClient = useKnockClient();

  return useCallback(
    async (actionType: ReviewPromptActionType) => {
      try {
        await knockClient.user.identify({
          [KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE]: new Date().toISOString(),
          [KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE]: actionType,
        });
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
      }
    },
    [knockClient]
  );
}
